import type {
  ConditionOperator,
  FilterCondition,
} from "schemas/generated/analyticsRead";

import * as React from "react";

import { Tooltip } from "@replo/design-system/components/tooltip/Tooltip";
import { useAnalyticsQueryContext } from "@v1/features/analytics/contexts/AnalyticsQueryContext";
import {
  createUrlParamsFilterOptions,
  OPERATOR_OPTIONS,
} from "@v1/features/analytics/moreFilters/constants";
import { findLabelForValue } from "@v1/features/analytics/moreFilters/utils";
import { X } from "lucide-react";

type AppliedFiltersTagProps = {
  attribute: string;
  filterCondition: FilterCondition;
  handleDeleteTag: any;
};

const AppliedFiltersTag: React.FC<AppliedFiltersTagProps> = ({
  attribute,
  filterCondition,
  handleDeleteTag,
}) => {
  const { operator, value } = filterCondition;

  const attributeLabel = findLabelForValue(
    createUrlParamsFilterOptions(),
    attribute,
  );

  const operatorLabel = findLabelForValue(OPERATOR_OPTIONS, operator);
  const operatorLabelLowercase = operatorLabel?.toLowerCase();

  return (
    <div className="text-default bg-surface flex flex-row items-center rounded-sm p-2 text-xs gap-1">
      <Tooltip triggerAsChild content={value} delay={800}>
        <div className="flex items-center max-w-[250px] overflow-hidden">
          <span className="flex-shrink-0">
            {attributeLabel}
            &nbsp;
            <span className="font-semibold">{operatorLabelLowercase}</span>
            &nbsp;
          </span>
          <span className="truncate flex-shrink min-w-0">{value}</span>
        </div>
      </Tooltip>
      <Tooltip triggerAsChild content="Remove filter" delay={800}>
        <span
          className="cursor-pointer"
          onClick={() => handleDeleteTag(attribute, operator, value)}
        >
          <X className="bg-surface h-4 w-4" />
        </span>
      </Tooltip>
    </div>
  );
};

const AppliedFiltersTags: React.FC = () => {
  const { dispatchAnalyticsQuery, query } = useAnalyticsQueryContext();

  const handleDeleteTag = (
    attribute: string,
    operator: ConditionOperator,
    value: string,
  ) => {
    dispatchAnalyticsQuery({
      type: "filters.deleteUrlParam",
      payload: {
        attribute,
        operator,
        value,
      },
    });
  };

  return (
    <>
      {Object.entries(query.filters.urlParams).map(
        ([attribute, filterConditions], i) => {
          return filterConditions.map((filterCondition) => {
            return (
              <AppliedFiltersTag
                key={i}
                attribute={attribute}
                filterCondition={filterCondition}
                handleDeleteTag={handleDeleteTag}
              />
            );
          });
        },
      )}
    </>
  );
};

export default AppliedFiltersTags;
